<template>
  <Doughnut
    :data="finalChartData"
    :options="finalChartOptions"
    :plugins="plugins"
    style="padding-bottom: 24px"
  />
</template>

<script lang="ts">
import 'chart.js/auto'
import { Doughnut } from 'vue-chartjs'
import DataLabelsPlugin from 'chartjs-plugin-datalabels'
import {
  type ChartOptions,
  type ChartData as FinalChartData,
  type Plugin
} from 'chart.js'
import { legendSpacing } from '~/assets/chart/plugins'
import { formatMemberValue } from '~/services/analyticsFormat'
import { FormatContext } from '~/types/analyticsFormat'
import { getColorPalette, buildOpenDrilldownEventBody } from '~/services/chart'
import {
  DATALABELS_PLUGIN_OPTIONS,
  DEFAULT_CHART_OPTIONS,
  SCALES_OPTIONS,
  TOOLTIP_PLUGIN_OPTIONS
} from '~/constants/chart'
import type {
  ChartData,
  DataSet,
  OpenDrilldownEventBody
} from '~/types/analytics'
import exportedColors from '~/assets/style/_exportDefaultChartColors.module.scss'

const LEGEND_INCREASE_WIDTH = 280
const LEGEND_INCREASE_MARGIN_LEFT = 40
const LegendSpacingPlugin = legendSpacing<'doughnut'>(
  LEGEND_INCREASE_WIDTH,
  0,
  LEGEND_INCREASE_MARGIN_LEFT
)

export default {
  name: 'ChartDoughnut',
  components: { Doughnut },
  props: {
    showDatalabels: {
      type: Boolean,
      required: true
    },
    chartData: {
      type: Object as PropType<ChartData>,
      required: true
    },
    isClickable: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    'open-drilldown': (_: OpenDrilldownEventBody) => true
  },
  setup() {
    return {
      plugins: [LegendSpacingPlugin, DataLabelsPlugin as Plugin<'doughnut'>]
    }
  },
  computed: {
    finalChartOptions(): ChartOptions<'doughnut'> {
      return {
        ...DEFAULT_CHART_OPTIONS,
        onHover: (event, chartElement) => {
          ;(event!.native!.target! as HTMLElement).style.cursor =
            chartElement[0] && this.isClickable ? 'pointer' : 'default'
        },
        onClick: (clickEvent, _, chart) => {
          const eventBody = buildOpenDrilldownEventBody(
            clickEvent,
            chart,
            this.chartData
          )

          if (eventBody) {
            this.$emit('open-drilldown', eventBody)
          }
        },
        scales: {
          x: {
            ...SCALES_OPTIONS.x,
            ticks: {
              ...SCALES_OPTIONS.x.ticks,
              display: false
            }
          },
          y: {
            ...SCALES_OPTIONS.y,
            ticks: {
              ...SCALES_OPTIONS.y.ticks,
              display: false
            },
            beginAtZero: false
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            reverse: false,
            labels: {
              font: {
                size: 14
              },
              pointStyle: 'rectRounded',
              usePointStyle: true
            }
          },
          tooltip: {
            ...TOOLTIP_PLUGIN_OPTIONS,
            enabled: true,
            callbacks: {
              title: tooltipItems => {
                return tooltipItems?.[0]?.label
              },
              label: context => {
                const label = context.dataset.label
                const format = (context.dataset as DataSet).format
                const value = context.parsed
                return `${label}: ${formatMemberValue(
                  value,
                  format,
                  FormatContext.CHART,
                  false
                )}`
              }
            }
          },
          datalabels: {
            ...DATALABELS_PLUGIN_OPTIONS,
            color: this.showDatalabels ? '#FFFFFF' : 'transparent',
            formatter: (value, context) => {
              // Hide null values
              if (value === null) {
                return ''
              }
              const format = (context.dataset as DataSet).format
              return formatMemberValue(
                value,
                format,
                FormatContext.CHART,
                false
              )
            }
          }
        }
      }
    },
    finalChartData(): FinalChartData<'doughnut'> {
      const length = this.chartData.datasets[0]!.data.filter(
        value => value !== null
      ).length
      const colors = getColorPalette(length, Object.values(exportedColors))

      return {
        ...this.chartData,
        labels: this.chartData.labels.map(label => label.formattedValue),
        datasets: this.chartData.datasets.map(dataset => {
          return {
            ...dataset,
            data: dataset.data as number[],
            stack: undefined,
            backgroundColor: colors,
            borderColor: colors,
            hoverBorderColor: '#141644',
            hoverBorderWidth: 4
          }
        })
      }
    }
  }
}
</script>
